import { EventEmitter } from "events";

// Update to export the type so it can be imported elsewhere
export type OperationType =
  | "LOCATION_UPDATE"
  | "QUANTITY_UPDATE"
  | "DATE_UPDATE"
  | "ITEM_REMOVAL"
  | "FEE_CALCULATION"; // Add FEE_CALCULATION to fix the error

export type OperationStatus = "PENDING" | "IN_PROGRESS" | "COMPLETE" | "ERROR";

interface CartOperation {
  id: string;
  type: OperationType;
  priority: number; // Higher number = higher priority
  status: OperationStatus;
  params: any;
  timestamp: number;
  groupIds?: string[];
  itemId?: string; // Add itemId to track specific items
}

/**
 * CartOperationManager
 *
 * A centralized service to manage cart operations and prevent race conditions.
 * This service enforces operation priorities and manages loading states.
 */
class CartOperationManager extends EventEmitter {
  private static instance: CartOperationManager;
  private operations: Map<string, CartOperation> = new Map();
  private activeOperation: CartOperation | null = null;

  // Priority levels for different operation types
  private readonly PRIORITY = {
    LOCATION_UPDATE: 100, // Highest priority
    QUANTITY_UPDATE: 50,
    ITEM_REMOVAL: 50,
    DATE_UPDATE: 10,
    FEE_CALCULATION: 30, // Add priority for FEE_CALCULATION
  };

  // Singleton pattern
  private constructor() {
    super();
  }

  public static getInstance(): CartOperationManager {
    if (!CartOperationManager.instance) {
      CartOperationManager.instance = new CartOperationManager();
    }
    return CartOperationManager.instance;
  }

  /**
   * Register a cart operation
   */
  public registerOperation(
    type: OperationType,
    params: any,
    groupIds?: string[],
    itemId?: string // Add itemId parameter
  ): string {
    const id = `${type}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    const operation: CartOperation = {
      id,
      type,
      priority: this.PRIORITY[type],
      status: "PENDING",
      params,
      timestamp: Date.now(),
      groupIds,
      itemId, // Store the itemId
    };

    this.operations.set(id, operation);
    console.log(
      `[CartOperationManager] Registered ${type} operation: ${id}${
        itemId ? ` for item ${itemId}` : ""
      }`
    );

    this.emit("operation:registered", operation);
    this.processNextOperation();

    return id;
  }

  /**
   * Start an operation
   */
  private startOperation(operationId: string): boolean {
    const operation = this.operations.get(operationId);

    if (!operation || operation.status !== "PENDING") {
      return false;
    }

    operation.status = "IN_PROGRESS";
    this.activeOperation = operation;

    console.log(
      `[CartOperationManager] Starting operation: ${operationId} (${operation.type})`
    );
    this.emit("operation:started", operation);

    return true;
  }

  /**
   * Complete an operation
   */
  public completeOperation(operationId: string, result?: any): void {
    const operation = this.operations.get(operationId);

    if (!operation) {
      console.warn(
        `[CartOperationManager] Cannot complete unknown operation: ${operationId}`
      );
      return;
    }

    operation.status = "COMPLETE";
    console.log(`[CartOperationManager] Completed operation: ${operationId}`);

    if (this.activeOperation?.id === operationId) {
      this.activeOperation = null;
    }

    this.emit("operation:completed", { operation, result });
    this.cleanupOldOperations();
    this.processNextOperation();
  }

  /**
   * Mark an operation as failed
   */
  public failOperation(operationId: string, error?: any): void {
    const operation = this.operations.get(operationId);

    if (!operation) {
      console.warn(
        `[CartOperationManager] Cannot fail unknown operation: ${operationId}`
      );
      return;
    }

    operation.status = "ERROR";
    console.error(
      `[CartOperationManager] Operation failed: ${operationId}`,
      error
    );

    if (this.activeOperation?.id === operationId) {
      this.activeOperation = null;
    }

    this.emit("operation:failed", { operation, error });
    this.cleanupOldOperations();
    this.processNextOperation();
  }

  /**
   * Process the next highest-priority operation
   */
  private processNextOperation(): void {
    // Skip if we already have an active operation
    if (this.activeOperation) {
      return;
    }

    // Find the highest priority pending operation
    let nextOperation: CartOperation | null = null;
    let highestPriority = -1;

    for (const operation of this.operations.values()) {
      if (
        operation.status === "PENDING" &&
        operation.priority > highestPriority
      ) {
        highestPriority = operation.priority;
        nextOperation = operation;
      }
    }

    if (nextOperation) {
      this.startOperation(nextOperation.id);
    }
  }

  /**
   * Clean up completed and old operations
   */
  private cleanupOldOperations(): void {
    const now = Date.now();
    const MAX_AGE = 30 * 60 * 1000; // 30 minutes

    for (const [id, operation] of this.operations.entries()) {
      if (
        operation.status === "COMPLETE" ||
        operation.status === "ERROR" ||
        now - operation.timestamp > MAX_AGE
      ) {
        this.operations.delete(id);
      }
    }
  }

  /**
   * Get all operations affecting a specific group
   */
  public getOperationsForGroup(groupId: string): CartOperation[] {
    const results: CartOperation[] = [];

    for (const operation of this.operations.values()) {
      if (
        operation.groupIds?.includes(groupId) ||
        operation.type === "LOCATION_UPDATE" // Location updates affect all groups
      ) {
        results.push(operation);
      }
    }

    return results;
  }

  /**
   * Get all operations affecting a specific item
   */
  public getOperationsForItem(itemId: string): CartOperation[] {
    const results: CartOperation[] = [];

    for (const operation of this.operations.values()) {
      if (operation.itemId === itemId || operation.type === "LOCATION_UPDATE") {
        results.push(operation);
      }
    }

    return results;
  }

  /**
   * Check if there are any in-progress operations of a given type
   */
  public hasActiveOperations(type?: OperationType): boolean {
    for (const operation of this.operations.values()) {
      if (operation.status === "IN_PROGRESS") {
        if (!type || operation.type === type) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get the current active operation
   */
  public getActiveOperation(): CartOperation | null {
    return this.activeOperation;
  }

  /**
   * Check if a group is currently being updated
   */
  public isGroupBeingUpdated(groupId: string): boolean {
    // Check if there's a location update in progress (affects all groups)
    if (this.hasActiveOperations("LOCATION_UPDATE")) {
      return true;
    }

    if (this.activeOperation?.groupIds?.includes(groupId)) {
      return true;
    }

    for (const operation of this.operations.values()) {
      if (
        operation.status === "IN_PROGRESS" &&
        operation.groupIds?.includes(groupId)
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a specific item is currently being updated
   */
  public isItemBeingUpdated(itemId: string): boolean {
    if (this.activeOperation?.itemId === itemId) {
      return true;
    }

    for (const operation of this.operations.values()) {
      if (
        operation.status === "IN_PROGRESS" &&
        (operation.itemId === itemId || operation.type === "LOCATION_UPDATE")
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gets all operations for all groups
   * @returns Record of group IDs to operations
   */
  public getAllGroupOperations(): Record<string, any[]> {
    // Get operations organized by group ID
    const groupOperations: Record<string, any[]> = {};

    // Loop through all registered operations
    for (const operation of this.operations.values()) {
      if (operation.groupIds) {
        for (const groupId of operation.groupIds) {
          if (!groupOperations[groupId]) {
            groupOperations[groupId] = [];
          }
          groupOperations[groupId].push(operation);
        }
      }
    }

    return groupOperations;
  }
}

// Export singleton instance
export default CartOperationManager.getInstance();
